import {
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type LayoutChangeEvent,
    AccessibilityInfo,
    Animated,
    Platform,
    View
} from "react-native";
import type IToastProps from "./type";
import type {
    ToastCloseConstantType,
    ToastSize
} from "./type";
import stylesheet, {
    getToastAnchorStyle,
    getToastMotion,
    useStyles
} from "./stylesheet";
import {
    useToastStackContext
} from "./context";
import {
    useToastStackMotion,
    useToastBackground,
    useToastSwipe
} from "./hook";
import {
    registerToastCloser,
    removeToastCloser
} from "./registry";
import {
    getToastStackRestTarget,
    getToastHiddenOffset,
    getToastExitOffset
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useReducedMotion,
    getMotionTiming
} from "../../helpers/motion";
import {
    useBreakpoint
} from "../../helpers/pointer";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    Portal
} from "../../helpers/portalize";
import Notice from "../notice";

const Toast: FC<IToastProps> = ({
    isCloseOnPressActionButton = true,
    isLiveRegionEnabled = true,
    spreadBehaviour = "free",
    isWorkWithPortal = true,
    contentContainerStyle,
    isCloseOnPress = true,
    isCloseOnSwipe = true,
    isShowAction = false,
    closeAnimationDelay,
    isAutoClose = true,
    openAnimationDelay,
    stackMode = "deck",
    isLoading = false,
    align = "center",
    isCompact = true,
    type = "neutral",
    secondaryAction,
    side = "bottom",
    autoCloseDelay,
    customLocalize,
    icon: IconProp,
    customTheme,
    isShowClose,
    edgeOffset,
    children,
    onClosed,
    subTitle,
    onPress,
    action,
    style,
    title,
    id
}) => {
    const {
        shapeConfigs,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const insets = useSafeAreaInsets();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isReduceMotionEnabled = useReducedMotion();

    const isBackground = useToastBackground();

    const breakpoint = useBreakpoint();

    const stack = useToastStackContext();

    const [
        isMeasured,
        setIsMeasured
    ] = useState(false);

    const [
        isClosing,
        setIsClosing
    ] = useState(false);

    const currentMotion = getToastMotion({
        isReduceMotionEnabled,
        closeAnimationDelay,
        openAnimationDelay,
        durations
    });

    const lastTarget = useRef(getToastStackRestTarget({
        layers: shapeConfigs.layers
    }));

    const stackTarget = stack?.layout[id];

    if(stackTarget) {
        lastTarget.current = stackTarget;
    }

    const target = lastTarget.current;

    const stackMotion = useToastStackMotion({
        customTheme,
        target
    });

    const presenceY = useRef(new Animated.Value(0)).current;

    const presenceOpacity = useRef(new Animated.Value(0)).current;

    const opacity = useMemo(() => Animated.multiply(stackMotion.opacity, presenceOpacity), []);

    const reflowY = useRef(new Animated.Value(0)).current;

    const layoutY = useRef<number | undefined>(undefined);

    const sizeRef = useRef<ToastSize>({
        height: 0,
        width: 0
    });

    const closeCallbacks = useRef<Array<(props: {
        id: string;
    }) => void>>([]);

    const isClosingRef = useRef(false);

    const isNativeDriver = Platform.OS !== "web";

    const actionTitle = isShowAction ? action?.title : undefined;

    const visibleSecondaryAction = isShowAction && secondaryAction && secondaryAction.title ? secondaryAction : undefined;

    const isLegacyClose = isShowAction && !action?.title && !secondaryAction;

    const isList = stackMode === "list";

    const isSwipeEnabled = isCloseOnSwipe && !isLoading && !isClosing;

    const isCollapsedBack = !!stack && !stack.isExpanded && !target.isFront;

    const handleClose = ({
        onClosed: _onClosed,
        swipe: swipeEnd
    }: ToastCloseConstantType = {
    }) => {
        if(_onClosed) {
            closeCallbacks.current.push(_onClosed);
        }

        if(isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;

        removeToastCloser(id);

        setIsClosing(true);

        stack?.onItemClosing({
            id
        });

        const animations = [
            Animated.timing(presenceOpacity, {
                duration: currentMotion.closeFadeDuration,
                easing: currentMotion.closeEasing,
                useNativeDriver: isNativeDriver,
                toValue: 0
            })
        ];

        if(!isReduceMotionEnabled && swipeEnd) {
            const isHorizontal = swipeEnd.axis === "x";

            animations.push(Animated.timing(isHorizontal ? swipe.swipeX : swipe.swipeY, {
                toValue: swipeEnd.amount + swipeEnd.sign * (isHorizontal ? sizeRef.current.width : sizeRef.current.height),
                duration: currentMotion.closeMoveDuration,
                easing: currentMotion.closeEasing,
                useNativeDriver: isNativeDriver
            }));
        }

        if(!isReduceMotionEnabled && !swipeEnd && !isList) {
            animations.push(Animated.timing(presenceY, {
                duration: currentMotion.closeMoveDuration,
                toValue: getToastExitOffset({
                    height: sizeRef.current.height,
                    isCollapsedBack,
                    side
                }),
                easing: currentMotion.closeEasing,
                useNativeDriver: isNativeDriver
            }));
        }

        Animated.parallel(animations).start(({
            finished
        }) => {
            if(!finished) {
                return;
            }

            if(onClosed) onClosed({
                id
            });

            closeCallbacks.current.splice(0).forEach((callback) => {
                callback({
                    id
                });
            });
        });
    };

    const swipe = useToastSwipe({
        onDismiss: (swipeEnd) => handleClose({
            swipe: swipeEnd
        }),
        isEnabled: isSwipeEnabled,
        isReduceMotionEnabled,
        direction,
        align,
        side
    });

    const {
        container: containerDynamicStyle,
        filler: fillerDynamicStyle,
        cover: coverDynamicStyle,
        slot: slotDynamicStyle
    } = useStyles({
        zIndex: isClosing ? target.zIndex + 1 : target.zIndex,
        isInteractive: !isClosing && target.isVisible,
        isCompactWidth: breakpoint === "compact",
        isSwiping: swipe.isSwiping,
        gap: spaces.spacingMd,
        spreadBehaviour,
        isSwipeEnabled,
        shapeConfigs,
        stackMode,
        radiuses,
        borders,
        colors,
        spaces,
        align,
        side
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const anchorDynamicStyle = getToastAnchorStyle({
        edgeOffset,
        stackMode,
        insets,
        spaces,
        align,
        side
    });

    const handleCloseRef = useRef(handleClose);

    useEffect(() => {
        handleCloseRef.current = handleClose;
    });

    useEffect(() => {
        return registerToastCloser(id, () => handleCloseRef.current());
    }, [
        id
    ]);

    useEffect(() => {
        if(isWorkWithPortal && Platform.OS === "ios") {
            AccessibilityInfo.announceForAccessibility(subTitle ? `${title}, ${subTitle}` : title);
        }

        return () => {
            presenceOpacity.stopAnimation();

            presenceY.stopAnimation();

            reflowY.stopAnimation();

            stack?.onItemRemove({
                id
            });
        };
    }, []);

    useEffect(() => {
        if(!isMeasured) {
            return;
        }

        presenceY.setValue(isReduceMotionEnabled ? 0 : isList ? spaces.spacingSm : getToastHiddenOffset({
            height: sizeRef.current.height,
            side
        }));

        Animated.parallel([
            Animated.timing(presenceOpacity, {
                duration: currentMotion.openFadeDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 1
            }),
            Animated.timing(presenceY, {
                duration: currentMotion.openMoveDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 0
            })
        ]).start();
    }, [
        isMeasured
    ]);

    const closeAnimation = (_onClosed?: (props: {
        id: string;
    }) => void) => {
        handleClose({
            onClosed: _onClosed
        });
    };

    const onLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            width,
            y
        } = event.nativeEvent.layout;

        const previousY = layoutY.current;

        layoutY.current = y;

        if(isList && previousY !== undefined && previousY !== y && !isClosingRef.current) {
            const reflowMotion = getMotionTiming({
                isReduceMotionEnabled,
                intent: "stack",
                durations
            });

            if(reflowMotion.duration > 0) {
                reflowY.stopAnimation((currentOffset) => {
                    reflowY.setValue(currentOffset + previousY - y);

                    Animated.timing(reflowY, {
                        duration: reflowMotion.duration,
                        useNativeDriver: isNativeDriver,
                        easing: reflowMotion.easing,
                        toValue: 0
                    }).start();
                });
            }
        }

        sizeRef.current = {
            height,
            width
        };

        stack?.onItemLayout({
            height,
            width,
            id
        });

        if(!isMeasured) {
            setIsMeasured(true);
        }
    };

    const onSurfacePress = () => {
        if(stack?.isTapExpandable) {
            stack.onExpandRequest();

            return;
        }

        if(onPress) onPress({
            id
        });

        if(isCloseOnPress) {
            closeAnimation();
        }
    };

    const onActionPress = () => {
        if(isCloseOnPressActionButton) {
            closeAnimation();
        }

        if(action) action.onPress({
            closeAnimation: ({
                onClosed: _onClosed
            }) => closeAnimation(_onClosed)
        });
    };

    const onSecondaryActionPress = () => {
        if(isCloseOnPressActionButton || !secondaryAction?.onPress) {
            closeAnimation();
        }

        if(secondaryAction?.onPress) secondaryAction.onPress({
            closeAnimation: ({
                onClosed: _onClosed
            }) => closeAnimation(_onClosed)
        });
    };

    const onClosePress = () => {
        if(!isLegacyClose || isCloseOnPressActionButton) {
            closeAnimation();
        }

        if(isLegacyClose && action) action.onPress({
            closeAnimation: ({
                onClosed: _onClosed
            }) => closeAnimation(_onClosed)
        });
    };

    const renderCover = () => {
        if(!stack) {
            return null;
        }

        return <Animated.View
            style={[
                stylesheet.cover,
                coverDynamicStyle,
                {
                    opacity: stackMotion.cover
                }
            ]}
            aria-hidden={true}
        />;
    };

    const renderFiller = () => {
        if(!stack) {
            return null;
        }

        return <View
            style={[
                stylesheet.filler,
                fillerDynamicStyle
            ]}
        />;
    };

    const renderToast = () => {
        const toast = <Animated.View
            {...(isSwipeEnabled ? swipe.panHandlers : {})}
            style={[
                containerStyle,
                stylesheet.container,
                containerDynamicStyle,
                {
                    transform: [
                        {
                            translateX: stackMotion.translateX
                        },
                        {
                            translateX: swipe.swipeX
                        },
                        {
                            translateY: stackMotion.translateY
                        },
                        {
                            translateY: presenceY
                        },
                        {
                            translateY: swipe.swipeY
                        },
                        {
                            translateY: reflowY
                        },
                        {
                            scaleX: stackMotion.scaleX
                        },
                        {
                            scaleY: stackMotion.scaleY
                        }
                    ],
                    opacity
                }
            ]}
            onLayout={onLayout}
        >
            <Notice
                onPress={onPress || isCloseOnPress || stack?.isTapExpandable ? onSurfacePress : undefined}
                isAutoClosePaused={isBackground || swipe.isSwiping || !!stack?.isPaused}
                onAutoClose={isAutoClose ? () => closeAnimation() : undefined}
                contentContainerStyle={contentContainerStyle}
                secondaryAction={visibleSecondaryAction ? {
                    style: visibleSecondaryAction.style,
                    title: visibleSecondaryAction.title,
                    icon: visibleSecondaryAction.icon,
                    onPress: onSecondaryActionPress
                } : undefined}
                isShowClose={isShowClose ?? isLegacyClose}
                isLiveRegionEnabled={isLiveRegionEnabled}
                action={action && actionTitle ? {
                    onPress: onActionPress,
                    style: action.style,
                    title: actionTitle,
                    icon: action.icon
                } : undefined}
                autoCloseDelay={autoCloseDelay}
                customLocalize={customLocalize}
                onClosePress={onClosePress}
                customTheme={customTheme}
                isLoading={isLoading}
                isCompact={isCompact}
                style={surfaceStyle}
                body={subTitle}
                icon={IconProp}
                title={title}
                type={type}
            >
                {children}
            </Notice>
            {renderCover()}
            {renderFiller()}
        </Animated.View>;

        if(!stack) {
            return toast;
        }

        return <View
            style={[
                stylesheet.slot,
                slotDynamicStyle
            ]}
        >
            {toast}
        </View>;
    };

    if(!isWorkWithPortal) {
        return renderToast();
    }

    return <Portal
        name="toast-system"
    >
        <View
            style={[
                stylesheet.anchor,
                anchorDynamicStyle
            ]}
        >
            {renderToast()}
        </View>
    </Portal>;
};
export default Toast;
